import { FieldInputProps, FormikValues } from 'formik';
import _get from 'lodash';
import {
	Condition,
	RULES as TYPE_RULES,
	IMPLICIT_RULES as TYPE_IMPLICIT_RULES,
	ValidationRule,
	Field,
	FieldNumber,
	FieldTime,
} from '../types/schema';
import { hhmmToNum, isArray, isDate, isEmpty, isNumeric, toDate, toNumber, toTimestamp } from '../helpers';
import { IMPLICIT_RULES } from '../constants';
import { ClientError, FormOptions } from '../types/general';

export interface FieldValidateArgs {
	values: FormikValues;
	value: any;
	schema: Field;
	options: FormOptions;
}

export interface ValidateArgs {
	values: FormikValues;
	options: FormOptions;
}

function isConditionBlockTrue(values: FormikValues, block: Condition[]) {
	return block.reduce((acc2, condition) => {
		// AND (if one is false, invalidate and return false)
		if (!acc2) return acc2;
		const res = resolveCondition(values, condition);
		if (res !== undefined) return res;
		return acc2;
	}, true);
}

export function validate(args: FieldValidateArgs) {
	const validation = findActiveRules(args);

	if (validation) {
		const errors = validation.reduce<ClientError[]>((acc, validationRule) => {
			const { rule } = validationRule;
			if (rules[rule]) {
				const res = rules[rule]({
					...args,
					rule: validationRule,
				});
				if (res !== true) {
					acc.push(res ? (typeof res === 'string' ? { key: res } : res) : { key: rule });
				}
			}
			return acc;
		}, []);

		return errors.length ? errors : true;
	}

	return true;
}

export function findActiveRules(args: Omit<FieldValidateArgs, 'value'>) {
	const { values, schema } = args;
	const { validation = [] } = schema;

	const { fieldType = 'text' } = schema;
	const newValidation = [...validation];
	// automatically attach rules based on field type
	if (IMPLICIT_RULES.includes(fieldType as TYPE_IMPLICIT_RULES)) {
		newValidation.push({ rule: fieldType as TYPE_IMPLICIT_RULES });
	}

	const res = newValidation.reduce<ValidationRule[]>((acc, validationRule) => {
		const { conditions } = validationRule;
		let mustValidate = true;
		if (conditions && !isConditionBlockTrue(values, conditions)) {
			mustValidate = false;
		}
		if (mustValidate) return [...acc, validationRule];
		return acc;
	}, []);
	return res;
}

function normalize(value: any) {
	if (typeof value === 'boolean') {
		value = value ? 'true' : 'false';
	}
	return `${value}`.toLowerCase();
}

export function resolveCondition(values: FormikValues, condition: Condition) {
	const { field, op, value } = condition;
	const current = normalize(values && values[field] ? values[field] : '');
	const compared = normalize(value || '');
	if (op === 'EQ') return current === compared;
	if (op === 'NOT') return current !== compared;
	if (op === '>' || op === '<') {
		if (!isNumeric(current)) return false;
		if (op === '>') return parseFloat(current) > parseFloat(compared);
		if (op === '<') return parseFloat(current) < parseFloat(compared);
	}
	if (op === 'IN') return compared.split(',').includes(current);
	if (op === 'NOT_IN') return !compared.split(',').includes(current);

	return undefined;
}

/** available rules */
interface ValidationRuleFunctionsArgs extends FieldValidateArgs {
	rule: ValidationRule;
}
type ValidationRuleFunctions = {
	[rule in TYPE_RULES]: (args: ValidationRuleFunctionsArgs) => boolean | string | ClientError;
};

export const rules: ValidationRuleFunctions = {
	required: function (args) {
		const { value } = args;
		if (value === false || isEmpty(value)) return false;
		return true;
	},
	compare: function (args) {
		const { value, values, schema, rule } = args;
		if (!value) return true;
		const { compare = '', op = '>' } = rule;
		let compareValue = `${compare}` as string;
		let errCompareValue = compareValue;
		if (compareValue.substring(0, 1) === '@') {
			// if starts with @ it links to another field value
			compareValue = values[compareValue.substring(1)] ?? '';
			errCompareValue = compareValue.substring(1);
		}
		if (op === 'EQ') {
			if (`${value}` !== `${compareValue}`) return { key: 'value_is_not_equal_than', value, errCompareValue };
		}
		if (isDate(value)) {
			// value is a date, compare no
			if (!isDate(compareValue)) return 'compare_is_not_date';
			// value is not greater
			if (op === '>' && toTimestamp(value) < toTimestamp(compareValue))
				return { key: 'value_is_date_not_recent_than', value, errCompareValue };
			if (op === '<' && toTimestamp(value) > toTimestamp(compareValue))
				return { key: 'value_is_date_not_oldest_than', value, errCompareValue };
		}
		if (isNumeric(value)) {
			// value is a number, compare no
			if (!isNumeric(compare)) return 'compare_is_not_number';
			// value is not greater
			if (op === '>' && toNumber(value) < toNumber(compareValue))
				return { key: 'value_is_number_not_greater_than', value, errCompareValue };
			if (op === '<' && toNumber(value) > toNumber(compareValue))
				return { key: 'value_is_number_not_less_than', value, errCompareValue };
		}
		return true;
	},
	text: function (args) {
		const { value, values, schema, options = {} } = args;
		const { textMaxLength } = options;
		if (textMaxLength) {
			if (value && value.length > textMaxLength) return { key: 'text_too_long', value, maxLength: textMaxLength };
		}
		return true;
	},
	number: function (args) {
		const { value, values, schema, rule } = args;
		const $schema = schema as FieldNumber;
		const range = $schema.props && $schema.props.range ? $schema.props.range : {};
		const num = parseFloat(`${value}`);
		if (range.min && num && num < range.min) {
			return { key: 'number_is_lesser_than_min', value: num, errCompareValue: range.min };
		}
		if (range.max && num && num > range.max) {
			return { key: 'number_is_greater_than_max', value: num, errCompareValue: range.max };
		}
		if (!value) return true;
		return isNumeric(value);
	},
	email: function (args) {
		const { value, values, schema, rule } = args;
		if (!value) return true;
		const regexEmail =
			// eslint-disable-next-line no-control-regex
			/(?:[a-z0-9+!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i;
		return !!value.match(regexEmail);
	},
	url: function (args) {
		const { value, values, schema, rule } = args;
		if (!value) return true;
		let url;
		try {
			url = new URL(value);
		} catch (_) {
			return false;
		}
		return url.protocol === 'http:' || url.protocol === 'https:';
	},
	date: function (args) {
		const { value, values, schema, rule } = args;
		if (!value) return true;
		return isDate(value);
	},
	time: function (args) {
		const { value, values, schema, rule } = args;
		const $schema = schema as FieldTime;
		const range = $schema.props && $schema.props.range ? $schema.props.range : {};
		const num = hhmmToNum(`${value}`);
		const min = hhmmToNum(range.min);
		const max = hhmmToNum(range.max);
		if (min && num && num < min) {
			return { key: 'time_is_lesser_than_min', value, errCompareValue: range.min };
		}
		if (max && num && num > max) {
			return { key: 'time_is_greater_than_max', value, errCompareValue: range.max };
		}
		return true;
	},
};
